import {useEffect, useState} from 'react'
import {Alert, Badge, Button, Card, Dropdown, Form, Modal, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import axios from 'axios'

import {useAuth} from '../../../../modules/auth'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {URL as BACKEND_URL} from '../../../gql/client'
import {IAllUserNode} from '../../../types/users'
import {Channel, ChannelType} from '../../../types/assignmentGroups'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
} from '../../channelConfig/hooks/useChannelConfig'
import {DifySection} from '../../channelConfig/components/DifySection'
import {PromptConfigSection} from '../../channelConfig/components/PromptConfigSection'
import {ChannelFormModal} from '../../assignmentGroups/components/ChannelFormModal'
import {useUserChannels} from '../hooks/useUserChannels'
import {confirmDelete} from '../../../store/confirmDialogStore'

interface Props {
  node: IAllUserNode
  userForm: any
  handleUserChange: (field: string, value: any) => void
  campaignOptions: VicidialCampaignOption[]
  savingUserConfig: boolean
  isUserDirty: boolean
  userId: string | undefined
  loadingUserConfig: boolean
  handleSaveUserConfig: () => void
  orgConfig: Partial<ChannelConfigData>
  userConfig: Partial<ChannelConfigData>
  showWebhookSecret: boolean
  setShowWebhookSecret: (value: boolean) => void
  handleResetToInherited: () => void
}

type TestChannel = 'whatsapp' | 'sms' | 'email'
type SettingModal = 'email' | 'policydial' | 'healthsherpa' | 'ai' | null

const CHANNEL_TYPE_LABELS: Record<ChannelType, {label: string; icon: string; color: string}> = {
  WHATSAPP: {label: 'WhatsApp', icon: 'bi-whatsapp', color: 'text-success'},
  SMS: {label: 'SMS', icon: 'bi-chat-dots', color: 'text-info'},
  VOICE: {label: 'Voice', icon: 'bi-telephone', color: 'text-primary'},
}

export const PersonalSettingsTab: React.FC<Props> = ({
  node,
  userForm,
  handleUserChange,
  campaignOptions,
  savingUserConfig,
  isUserDirty,
  userId,
  loadingUserConfig,
  handleSaveUserConfig,
  orgConfig,
  userConfig,
  showWebhookSecret,
  setShowWebhookSecret,
  handleResetToInherited,
}) => {
  const {auth} = useAuth()
  const [testModal, setTestModal] = useState<TestChannel | null>(null)
  const [testRecipient, setTestRecipient] = useState('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{ok: boolean; message: string} | null>(null)
  const [settingModal, setSettingModal] = useState<SettingModal>(null)

  const {
    channels,
    loading: loadingChannels,
    saving: savingChannel,
    saveChannel,
    deleteChannel,
  } = useUserChannels(userId)
  const [channelModalOpen, setChannelModalOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  const openTestModal = (channel: TestChannel) => {
    setTestModal(channel)
    setTestRecipient('')
    setTestResult(null)
  }

  const handleTestConnection = async () => {
    if (!testRecipient.trim()) {
      toast.error('Enter a recipient')
      return
    }
    setTestLoading(true)
    setTestResult(null)
    try {
      const token = getToken()
      const endpoint =
        testModal === 'whatsapp' ? 'test-whatsapp' : testModal === 'sms' ? 'test-sms' : 'test-email'
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/channel-config/${endpoint}`,
        {
          recipient: testRecipient,
          user_id: userId,
        },
        {headers: {Authorization: `Bearer ${token}`}}
      )
      setTestResult({
        ok: res.data?.ok ?? true,
        message: res.data?.message || 'Test sent successfully',
      })
    } catch (error: any) {
      setTestResult({
        ok: false,
        message: error.response?.data?.error || 'Test failed',
      })
    } finally {
      setTestLoading(false)
    }
  }

  const usedChannelTypes = channels.map((ch) => ch.channel_type)
  const canAddChannel = usedChannelTypes.length < 2 // 1 WhatsApp + 1 SMS max per user

  // "Configured" reflects whether the section has ever been set up (has saved data),
  // not whether it's currently enabled — flipping the switch off must not make the
  // row (and its saved config) disappear back into the Add menu.
  const emailConfigured = !!(
    userForm.email_enabled ||
    userForm.email_host ||
    userForm.email_host_user ||
    userForm.email_from
  )
  const policyDialConfigured = !!(
    userForm.vicidial_enabled ||
    userForm.telephony_provider ||
    userForm.vicidial_agent_user ||
    userForm.vicidial_phone_login
  )
  const healthSherpaConfigured = !!(
    userForm.healthsherpa_enabled ||
    userForm.healthsherpa_email ||
    userForm.healthsherpa_api_key
  )
  const aiConfigured = !!(
    userForm.dify_enabled ||
    userForm.dify_api_key ||
    userForm.dify_app_id ||
    userForm.llm_api_key ||
    userForm.llm_model
  )

  const telephonyIconSrc =
    userForm.telephony_provider === 'RINGCENTRAL'
      ? '/media/tfi/ringcentral-mark.svg'
      : '/media/tfi/policydial-icon.svg'
  const telephonyLabel =
    userForm.telephony_provider === 'RINGCENTRAL' ? 'RingCentral' : 'PolicyDial'

  const handleDeleteTelephony = async () => {
    const ok = await confirmDelete('This user will inherit the organization telephony settings.', {
      title: 'Remove telephony override',
    })
    if (!ok) return
    handleResetToInherited()
    toast.success('Telephony override removed')
  }

  const handleDeleteEmail = async () => {
    const ok = await confirmDelete('This will clear the personal SMTP override for this agent.', {
      title: 'Remove Email (SMTP) override',
    })
    if (!ok) return
    handleUserChange('email_enabled', false)
    handleUserChange('email_host', '')
    handleUserChange('email_port', 587)
    handleUserChange('email_host_user', '')
    handleUserChange('email_host_password', '')
    handleUserChange('email_use_tls', true)
    handleUserChange('email_from', '')
    handleUserChange('email_reply_to', '')
    toast.success('Email override removed')
  }

  const handleDeleteHealthSherpa = async () => {
    const ok = await confirmDelete(
      'This will clear the personal HealthSherpa credentials for this agent.',
      {
        title: 'Remove HealthSherpa override',
      }
    )
    if (!ok) return
    handleUserChange('healthsherpa_enabled', false)
    handleUserChange('healthsherpa_email', '')
    handleUserChange('healthsherpa_api_key', '')
    handleUserChange('healthsherpa_webhook_secret', '')
    toast.success('HealthSherpa override removed')
  }

  const handleDeleteAi = async () => {
    const ok = await confirmDelete(
      'This will clear the personal AI (Dify) configuration for this agent.',
      {
        title: 'Remove AI (Dify) override',
      }
    )
    if (!ok) return
    handleUserChange('dify_enabled', false)
    handleUserChange('dify_api_key', '')
    handleUserChange('dify_api_url', 'https://api.dify.ai/v1')
    handleUserChange('dify_app_id', '')
    handleUserChange('ai_provider', 'DIFY')
    handleUserChange('llm_provider', 'ANTHROPIC')
    handleUserChange('llm_api_key', '')
    handleUserChange('llm_model', '')
    handleUserChange('prompt_body', '')
    handleUserChange('share_contact_info', null)
    handleUserChange('share_quotes', null)
    handleUserChange('share_policies', null)
    handleUserChange('share_leads', null)
    handleUserChange('share_consent_status', null)
    toast.success('AI override removed')
  }

  const addableSettings: Array<{
    key: Exclude<SettingModal, null>
    label: string
    icon?: string
    iconSrc?: string
  }> = [
    ...(!emailConfigured
      ? [{key: 'email' as const, label: 'Email SMTP', icon: 'bi-envelope'}]
      : []),
    ...(!healthSherpaConfigured
      ? [{key: 'healthsherpa' as const, label: 'HealthSherpa', icon: 'bi-heart-pulse'}]
      : []),
    ...(!aiConfigured ? [{key: 'ai' as const, label: 'AI (Dify)', icon: 'bi-robot'}] : []),
  ]

  return (
    <>
      <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
        <div>
          <p className='text-muted fs-7 mb-0'>
            Overrides exclusive to this agent. These take priority over organization defaults.
          </p>
        </div>
        <Button
          variant='primary'
          onClick={handleSaveUserConfig}
          disabled={savingUserConfig || !isUserDirty || !userId}
        >
          {savingUserConfig ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            <>
              <i className='bi bi-check-lg me-1' />
              Save Personal Settings
            </>
          )}
        </Button>
      </div>

      {!userId ? (
        <Alert variant='warning' className='mb-0'>
          Save the user first before configuring personal settings.
        </Alert>
      ) : loadingUserConfig ? (
        <div className='d-flex justify-content-center align-items-center' style={{minHeight: 180}}>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        <>
          {/* ─── Communication Channels ─── */}
          <Card className='mb-4'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='fw-bold text-gray-800 mb-0'>
                  <i className='bi bi-broadcast me-2' />
                  Communication Channels
                </h5>
                <div className='d-flex gap-2'>
                  <Button
                    variant='primary'
                    size='sm'
                    disabled={!canAddChannel}
                    title={
                      !canAddChannel
                        ? 'This user already has a WhatsApp and an SMS channel'
                        : undefined
                    }
                    onClick={() => {
                      setEditingChannel(null)
                      setChannelModalOpen(true)
                    }}
                  >
                    <i className='bi bi-plus-lg me-1' /> Add Channel
                  </Button>
                  {!policyDialConfigured && (
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() => setSettingModal('policydial')}
                    >
                      <i className='bi bi-plus-lg me-1' /> Add Telephony
                    </Button>
                  )}
                </div>
              </div>

              {loadingChannels ? (
                <div className='d-flex justify-content-center py-5'>
                  <Spinner animation='border' size='sm' />
                </div>
              ) : channels.length === 0 ? (
                <div className='text-muted text-center py-5'>
                  No personal channels configured — this agent inherits the organization's channels.
                </div>
              ) : (
                <div className='table-responsive'>
                  <Table size='sm' hover className='align-middle mb-0'>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Provider</th>
                        <th>Number</th>
                        <th>Status</th>
                        <th>Webhook</th>
                        <th className='text-end'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channels.map((ch) => {
                        const meta = CHANNEL_TYPE_LABELS[ch.channel_type]
                        const webhookUrl =
                          ch.provider === 'TWILIO'
                            ? ch.webhook_url_twilio
                            : ch.provider === 'BOTMAKER'
                            ? ch.webhook_url_botmaker
                            : `${BACKEND_URL}/webhooks/meta/${ch.webhook_token}/`
                        return (
                          <tr key={ch.id}>
                            <td>
                              <i className={`bi ${meta.icon} ${meta.color} me-1`} />
                              {meta.label}
                            </td>
                            <td>{ch.provider}</td>
                            <td>
                              <code>{ch.phone_number || '—'}</code>
                            </td>
                            <td>
                              <span
                                className={`badge ${ch.is_active ? 'bg-success' : 'bg-secondary'}`}
                              >
                                {ch.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {webhookUrl && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  style={{padding: '0.25rem'}}
                                  onClick={() => {
                                    navigator.clipboard.writeText(webhookUrl)
                                    toast.success('Webhook URL copied')
                                  }}
                                  title='Copy webhook URL'
                                >
                                  <i className='bi bi-clipboard' />
                                </Button>
                              )}
                            </td>
                            <td className='text-end'>
                              {ch.channel_type !== 'VOICE' && (
                                <Button
                                  variant='outline-secondary'
                                  size='sm'
                                  className='me-1'
                                  onClick={() =>
                                    openTestModal(
                                      ch.channel_type === 'WHATSAPP' ? 'whatsapp' : 'sms'
                                    )
                                  }
                                >
                                  <i className='bi bi-send-check' />
                                </Button>
                              )}
                              <Button
                                variant='outline-secondary'
                                size='sm'
                                className='me-1'
                                onClick={() => {
                                  setEditingChannel(ch)
                                  setChannelModalOpen(true)
                                }}
                              >
                                <i className='bi bi-pencil' />
                              </Button>
                              <Button
                                variant='outline-danger'
                                size='sm'
                                onClick={async () => {
                                  const confirmed = await confirmDelete(
                                    'This channel configuration will be permanently removed.',
                                    {title: 'Delete channel'}
                                  )
                                  if (confirmed) {
                                    const ok = await deleteChannel(ch.id!)
                                    if (ok) toast.success('Channel deleted')
                                    else toast.error('Error deleting channel')
                                  }
                                }}
                              >
                                <i className='bi bi-trash' />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {policyDialConfigured && (
                <div className={channels.length > 0 ? 'mt-3 pt-3 border-top' : 'mt-3'}>
                  <div className='list-group list-group-flush'>
                    <SettingRow
                      iconSrc={telephonyIconSrc}
                      label={telephonyLabel}
                      summary='Telephony override enabled'
                      onEdit={() => setSettingModal('policydial')}
                      enabled={!!userForm.vicidial_enabled}
                      onToggleEnabled={(value) => handleUserChange('vicidial_enabled', value)}
                      onDelete={handleDeleteTelephony}
                    />
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <ChannelFormModal
            isOpen={channelModalOpen}
            onClose={() => setChannelModalOpen(false)}
            onSave={async (data) => {
              try {
                const saved = await saveChannel(data)
                if (saved) toast.success('Channel saved')
                return saved as any
              } catch (error: any) {
                toast.error(error?.message || 'Error saving channel')
                throw error
              }
            }}
            saving={savingChannel}
            initialData={editingChannel}
            groupMembers={[]}
            availableProviders={['TWILIO', 'BOTMAKER', 'META']}
            excludeChannelTypes={usedChannelTypes}
          />

          {/* ─── Other Settings ─── */}
          <Card>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='fw-bold text-gray-800 mb-0'>Other Settings</h5>
                {addableSettings.length > 0 && (
                  <Dropdown>
                    <Dropdown.Toggle variant='primary' size='sm'>
                      <i className='bi bi-plus-lg me-1' /> Add
                    </Dropdown.Toggle>
                    <Dropdown.Menu align='end'>
                      {addableSettings.map((item) => (
                        <Dropdown.Item key={item.key} onClick={() => setSettingModal(item.key)}>
                          {item.iconSrc ? (
                            <img
                              src={item.iconSrc}
                              alt=''
                              className='me-2'
                              style={{width: '16px', height: '16px'}}
                            />
                          ) : (
                            <i className={`bi ${item.icon} me-2`} />
                          )}
                          {item.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>

              {!emailConfigured && !healthSherpaConfigured && !aiConfigured ? (
                <div className='text-muted text-center py-5'>
                  No additional settings configured for this agent yet.
                </div>
              ) : (
                <div className='list-group list-group-flush'>
                  {emailConfigured && (
                    <SettingRow
                      icon='bi-envelope'
                      color='text-primary'
                      label='Email (SMTP)'
                      summary={userForm.email_host || 'Configured'}
                      onEdit={() => setSettingModal('email')}
                      enabled={!!userForm.email_enabled}
                      onToggleEnabled={(value) => handleUserChange('email_enabled', value)}
                      onDelete={handleDeleteEmail}
                    />
                  )}
                  {healthSherpaConfigured && (
                    <SettingRow
                      icon='bi-heart-pulse'
                      color='text-danger'
                      label='HealthSherpa'
                      summary={userForm.healthsherpa_email || 'Configured'}
                      onEdit={() => setSettingModal('healthsherpa')}
                      enabled={!!userForm.healthsherpa_enabled}
                      onToggleEnabled={(value) => handleUserChange('healthsherpa_enabled', value)}
                      onDelete={handleDeleteHealthSherpa}
                    />
                  )}
                  {aiConfigured && (
                    <SettingRow
                      icon='bi-robot'
                      color='text-success'
                      label='AI (Dify)'
                      summary={
                        userForm.ai_provider === 'LLM'
                          ? `LLM Directo (${userForm.llm_provider})`
                          : 'Dify'
                      }
                      onEdit={() => setSettingModal('ai')}
                      enabled={!!userForm.dify_enabled}
                      onToggleEnabled={(value) => handleUserChange('dify_enabled', value)}
                      onDelete={handleDeleteAi}
                    />
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* ─── Email SMTP Modal ─── */}
          <Modal show={settingModal === 'email'} onHide={() => setSettingModal(null)} size='lg'>
            <Modal.Header closeButton>
              <Modal.Title className='fs-6'>
                <i className='bi bi-envelope me-2' />
                Email (SMTP)
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Check
                type='switch'
                id='user_email_enabled'
                label='Enable SMTP override'
                checked={userForm.email_enabled}
                onChange={(e) => handleUserChange('email_enabled', e.target.checked)}
                className='fw-semibold mb-3'
              />
              {userForm.email_enabled && (
                <div className='row g-3'>
                  <div className='col-12'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>SMTP Host</Form.Label>
                      <Form.Control
                        type='text'
                        value={userForm.email_host || ''}
                        onChange={(e) => handleUserChange('email_host', e.target.value)}
                        placeholder='smtp.gmail.com'
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>Port</Form.Label>
                      <Form.Control
                        type='number'
                        value={userForm.email_port || 587}
                        onChange={(e) =>
                          handleUserChange(
                            'email_port',
                            e.target.value === '' ? 587 : Number(e.target.value)
                          )
                        }
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6 d-flex align-items-end'>
                    <Form.Check
                      type='switch'
                      id='user_email_use_tls'
                      label='Use TLS'
                      checked={userForm.email_use_tls}
                      onChange={(e) => handleUserChange('email_use_tls', e.target.checked)}
                      className='mb-0 fw-semibold fs-8'
                    />
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Username / Email
                      </Form.Label>
                      <Form.Control
                        type='text'
                        value={userForm.email_host_user || ''}
                        onChange={(e) => handleUserChange('email_host_user', e.target.value)}
                        placeholder='user@gmail.com'
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>Password</Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        value={userForm.email_host_password || ''}
                        onChange={(e) => handleUserChange('email_host_password', e.target.value)}
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        From Address
                      </Form.Label>
                      <Form.Control
                        type='email'
                        value={userForm.email_from || ''}
                        onChange={(e) => handleUserChange('email_from', e.target.value)}
                        placeholder='Agent <agent@agency.com>'
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Reply-To (Optional)
                      </Form.Label>
                      <Form.Control
                        type='email'
                        value={userForm.email_reply_to || ''}
                        onChange={(e) => handleUserChange('email_reply_to', e.target.value)}
                        placeholder='support@agency.com'
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-12'>
                    <Button
                      variant='outline-primary'
                      size='sm'
                      className='w-100'
                      onClick={() => openTestModal('email')}
                    >
                      <i className='bi bi-send-check me-2'></i>Test Connection
                    </Button>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setSettingModal(null)}>
                Done
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ─── PolicyDial Modal ─── */}
          <Modal
            show={settingModal === 'policydial'}
            onHide={() => setSettingModal(null)}
            size='lg'
          >
            <Modal.Header closeButton>
              <Modal.Title className='fs-6 d-flex align-items-center'>
                <i className='bi bi-telephone-forward me-2' />
                Telephony System
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className='row g-3'>
                <div className='col-12'>
                  <div className='d-flex align-items-end gap-3'>
                    <Form.Group className='mb-0 flex-grow-1'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Telephony Provider
                      </Form.Label>
                      <Form.Select
                        value={userForm.telephony_provider || ''}
                        onChange={(e) =>
                          handleUserChange(
                            'telephony_provider',
                            e.target.value as TelephonyProviderValue
                          )
                        }
                        size='sm'
                      >
                        <option value=''>Inherit organization</option>
                        <option value='RINGCENTRAL'>RingCentral</option>
                        <option value='VICIDIAL'>PolicyDial</option>
                      </Form.Select>
                    </Form.Group>
                    {userForm.telephony_provider === 'VICIDIAL' && (
                      <img
                        src='/media/tfi/policydial-webphone.svg'
                        alt='PolicyDial'
                        style={{height: '32px'}}
                      />
                    )}
                  </div>
                </div>
                <div className='col-12'>
                  <Form.Check
                    type='switch'
                    id='user_vicidial_enabled'
                    label='Enable PolicyDial override'
                    checked={userForm.vicidial_enabled}
                    onChange={(e) => handleUserChange('vicidial_enabled', e.target.checked)}
                    className='fw-semibold'
                  />
                </div>
                {userForm.vicidial_enabled && (
                  <>
                    <div className='col-12'>
                      <h6 className='fw-bold text-info fs-8 mb-2'>Agent Credentials</h6>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>
                          Agent User
                        </Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.vicidial_agent_user}
                          onChange={(e) => handleUserChange('vicidial_agent_user', e.target.value)}
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>
                          Agent Password
                        </Form.Label>
                        <Form.Control
                          type='password'
                          autoComplete='new-password'
                          value={userForm.vicidial_agent_password}
                          onChange={(e) =>
                            handleUserChange('vicidial_agent_password', e.target.value)
                          }
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>
                          Extension / Phone Login
                        </Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.vicidial_phone_login}
                          onChange={(e) => handleUserChange('vicidial_phone_login', e.target.value)}
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>
                          Phone Password
                        </Form.Label>
                        <Form.Control
                          type='password'
                          autoComplete='new-password'
                          value={userForm.vicidial_phone_password}
                          onChange={(e) =>
                            handleUserChange('vicidial_phone_password', e.target.value)
                          }
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                    <div className='col-12'>
                      <h6 className='fw-bold text-success fs-8 mb-2'>Campaign Routing</h6>
                    </div>
                    <div className='col-12'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>Campaign</Form.Label>
                        {campaignOptions.length > 0 ? (
                          <Form.Select
                            value={userForm.vicidial_campaign_id}
                            onChange={(e) =>
                              handleUserChange('vicidial_campaign_id', e.target.value)
                            }
                            size='sm'
                          >
                            <option value=''>
                              Inherit org{' '}
                              {orgConfig.vicidial_campaign_id
                                ? `(${orgConfig.vicidial_campaign_id})`
                                : ''}
                            </option>
                            {campaignOptions.map((campaign) => (
                              <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                {campaign.campaign_id} - {campaign.campaign_name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type='text'
                            placeholder='Inherit organization'
                            value={userForm.vicidial_campaign_id}
                            onChange={(e) =>
                              handleUserChange('vicidial_campaign_id', e.target.value)
                            }
                            size='sm'
                          />
                        )}
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>
                          Auto Dial Delay (seconds)
                        </Form.Label>
                        <Form.Control
                          type='number'
                          min={0}
                          step={1}
                          placeholder={
                            orgConfig.vicidial_dial_queue_delay_seconds != null
                              ? String(orgConfig.vicidial_dial_queue_delay_seconds)
                              : '5'
                          }
                          value={userForm.vicidial_dial_queue_delay_seconds ?? ''}
                          onChange={(e) =>
                            handleUserChange(
                              'vicidial_dial_queue_delay_seconds',
                              e.target.value === '' ? null : Math.max(0, Number(e.target.value))
                            )
                          }
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                    <div className='col-12'>
                      <Button
                        variant='light'
                        size='sm'
                        className='w-100'
                        onClick={handleResetToInherited}
                      >
                        <i className='bi bi-arrow-clockwise me-1'></i>Reset to Inherited
                      </Button>
                    </div>
                  </>
                )}
                <div className='col-12'>
                  <hr className='my-1' />
                  <Form.Check
                    type='switch'
                    id='twilio_provisioning_enabled'
                    label='Allow self-provisioning of Caller IDs (My Numbers)'
                    checked={!!userForm.twilio_provisioning_enabled}
                    onChange={(e) =>
                      handleUserChange('twilio_provisioning_enabled', e.target.checked)
                    }
                    className='fw-semibold'
                  />
                  <div className='text-muted fs-8 mt-1'>
                    Shows the “My Numbers” tab in this user’s Profile so they can buy their own
                    Twilio Caller IDs (inbound + outbound).
                  </div>
                </div>
                {userForm.twilio_provisioning_enabled && (
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Max numbers
                      </Form.Label>
                      <Form.Control
                        type='number'
                        min={1}
                        step={1}
                        placeholder='2'
                        value={userForm.twilio_provisioning_max_numbers ?? 2}
                        onChange={(e) =>
                          handleUserChange(
                            'twilio_provisioning_max_numbers',
                            e.target.value === '' ? 1 : Math.max(1, Number(e.target.value))
                          )
                        }
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setSettingModal(null)}>
                Done
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ─── HealthSherpa Modal ─── */}
          <Modal
            show={settingModal === 'healthsherpa'}
            onHide={() => setSettingModal(null)}
            size='lg'
          >
            <Modal.Header closeButton>
              <Modal.Title className='fs-6'>
                <i className='bi bi-heart-pulse text-danger me-2' />
                HealthSherpa
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Check
                type='switch'
                id='user_healthsherpa_enabled'
                label='Use personal HealthSherpa credentials'
                checked={userForm.healthsherpa_enabled || false}
                onChange={(e) => handleUserChange('healthsherpa_enabled', e.target.checked)}
                className='fw-semibold mb-3'
              />
              {userForm.healthsherpa_enabled && (
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Agent Email
                      </Form.Label>
                      <Form.Control
                        type='email'
                        placeholder='agent@example.com'
                        value={userForm.healthsherpa_email || ''}
                        onChange={(e) => handleUserChange('healthsherpa_email', e.target.value)}
                        size='sm'
                      />
                      <Form.Text className='text-muted'>Must match registered agent</Form.Text>
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>API Key</Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='off'
                        placeholder='Enter API key'
                        value={userForm.healthsherpa_api_key || ''}
                        onChange={(e) => handleUserChange('healthsherpa_api_key', e.target.value)}
                        size='sm'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-12'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>
                        Webhook Secret
                      </Form.Label>
                      <div className='d-flex gap-1 align-items-start'>
                        <Form.Control
                          type={showWebhookSecret ? 'text' : 'password'}
                          autoComplete='off'
                          placeholder='Secret'
                          value={userForm.healthsherpa_webhook_secret || ''}
                          onChange={(e) =>
                            handleUserChange('healthsherpa_webhook_secret', e.target.value)
                          }
                          size='sm'
                        />
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          disabled={!userForm.healthsherpa_webhook_secret}
                          className='mt-1'
                          style={{minWidth: '28px', padding: '0.25rem'}}
                        >
                          <i
                            className={`bi ${showWebhookSecret ? 'bi-eye-slash' : 'bi-eye'} fs-8`}
                          ></i>
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => {
                            navigator.clipboard.writeText(
                              userForm.healthsherpa_webhook_secret || ''
                            )
                            toast.success('Copied')
                          }}
                          disabled={!userForm.healthsherpa_webhook_secret}
                          className='mt-1'
                          style={{minWidth: '28px', padding: '0.25rem'}}
                        >
                          <i className='bi bi-clipboard fs-8'></i>
                        </Button>
                      </div>
                    </Form.Group>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setSettingModal(null)}>
                Done
              </Button>
            </Modal.Footer>
          </Modal>

          {/* ─── AI (Dify) Modal ───
              No Modal.Title here on purpose: DifySection renders its own header
              (🤖 title + status badge) — a Modal.Title would just duplicate it. */}
          <Modal show={settingModal === 'ai'} onHide={() => setSettingModal(null)} size='lg'>
            <Modal.Header closeButton />
            <Modal.Body>
              <DifySection
                difyEnabled={userForm.dify_enabled || false}
                difyApiKey={userForm.dify_api_key || ''}
                difyApiUrl={userForm.dify_api_url || 'https://api.dify.ai/v1'}
                difyAppId={userForm.dify_app_id || ''}
                onDifyEnabledChange={(value) => handleUserChange('dify_enabled', value)}
                onDifyApiKeyChange={(value) => handleUserChange('dify_api_key', value)}
                onDifyApiUrlChange={(value) => handleUserChange('dify_api_url', value)}
                onDifyAppIdChange={(value) => handleUserChange('dify_app_id', value)}
                organizationId={userId || ''}
                switchLabel='Habilitar IA Conversacional para mis chats'
                aiProvider={userForm.ai_provider || 'DIFY'}
                onAiProviderChange={(value) => handleUserChange('ai_provider', value)}
                llmProvider={userForm.llm_provider || 'ANTHROPIC'}
                onLlmProviderChange={(value) => handleUserChange('llm_provider', value)}
                llmApiKey={userForm.llm_api_key || ''}
                onLlmApiKeyChange={(value) => handleUserChange('llm_api_key', value)}
                llmModel={userForm.llm_model || ''}
                onLlmModelChange={(value) => handleUserChange('llm_model', value)}
              />
              {userForm.dify_enabled && (
                <div className='pt-4 border-top mt-4'>
                  <PromptConfigSection
                    isOrgScope={false}
                    promptHeader={userForm.prompt_header || ''}
                    onPromptHeaderChange={(value) => handleUserChange('prompt_header', value)}
                    promptBody={userForm.prompt_body || ''}
                    onPromptBodyChange={(value) => handleUserChange('prompt_body', value)}
                    promptFooter={userForm.prompt_footer || ''}
                    onPromptFooterChange={(value) => handleUserChange('prompt_footer', value)}
                    shareContactInfo={userForm.share_contact_info ?? null}
                    onShareContactInfoChange={(value) =>
                      handleUserChange('share_contact_info', value)
                    }
                    shareQuotes={userForm.share_quotes ?? null}
                    onShareQuotesChange={(value) => handleUserChange('share_quotes', value)}
                    sharePolicies={userForm.share_policies ?? null}
                    onSharePoliciesChange={(value) => handleUserChange('share_policies', value)}
                    shareLeads={userForm.share_leads ?? null}
                    onShareLeadsChange={(value) => handleUserChange('share_leads', value)}
                    shareConsentStatus={userForm.share_consent_status ?? null}
                    onShareConsentStatusChange={(value) =>
                      handleUserChange('share_consent_status', value)
                    }
                  />
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setSettingModal(null)}>
                Done
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {/* Test Connection Modal */}
      <Modal show={!!testModal} onHide={() => setTestModal(null)} centered size='sm'>
        <Modal.Header closeButton>
          <Modal.Title className='fs-6'>
            {testModal === 'whatsapp' && (
              <>
                <i className='bi bi-whatsapp text-success me-2'></i>Test WhatsApp
              </>
            )}
            {testModal === 'sms' && (
              <>
                <i className='bi bi-chat-dots text-info me-2'></i>Test SMS
              </>
            )}
            {testModal === 'email' && (
              <>
                <i className='bi bi-envelope text-primary me-2'></i>Test Email
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              {testModal === 'email' ? 'Destination email' : 'Destination number'}
            </Form.Label>
            <Form.Control
              type={testModal === 'email' ? 'email' : 'tel'}
              placeholder={testModal === 'email' ? 'you@example.com' : '+1 305 555 0100'}
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTestConnection()}
            />
            {testModal !== 'email' && (
              <Form.Text className='text-muted'>Include country code, e.g. +1 for USA.</Form.Text>
            )}
          </Form.Group>
          {testResult && (
            <div
              className={`alert mt-3 py-2 small ${
                testResult.ok ? 'alert-success' : 'alert-danger'
              }`}
            >
              <i className={`bi ${testResult.ok ? 'bi-check-circle' : 'bi-x-circle'} me-2`}></i>
              {testResult.message}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className='py-2'>
          <Button variant='outline-secondary' size='sm' onClick={() => setTestModal(null)}>
            Close
          </Button>
          <Button
            variant={
              testModal === 'whatsapp' ? 'success' : testModal === 'sms' ? 'info' : 'primary'
            }
            size='sm'
            onClick={handleTestConnection}
            disabled={testLoading || !testRecipient.trim()}
          >
            {testLoading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' />
                Sending...
              </>
            ) : (
              <>
                <i className='bi bi-send me-1'></i>Send Test
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const SettingRow: React.FC<{
  icon?: string
  iconSrc?: string
  color?: string
  label: string
  summary: string
  onEdit: () => void
  enabled: boolean
  onToggleEnabled: (value: boolean) => void
  onDelete?: () => void
}> = ({icon, iconSrc, color, label, summary, onEdit, enabled, onToggleEnabled, onDelete}) => (
  <div className='list-group-item d-flex justify-content-between align-items-center px-0'>
    <div className='d-flex align-items-center gap-2'>
      {iconSrc ? (
        <img src={iconSrc} alt={label} style={{width: '24px', height: '24px'}} />
      ) : (
        <i className={`bi ${icon} ${color} fs-4`} />
      )}
      <div>
        <div className='fw-semibold'>{label}</div>
        <div className='text-muted fs-8'>{enabled ? summary : 'Disabled'}</div>
      </div>
    </div>
    <div className='d-flex align-items-center gap-3'>
      <Form.Check
        type='switch'
        id={`setting-row-enabled-${label}`}
        checked={enabled}
        onChange={(e) => onToggleEnabled(e.target.checked)}
        title={enabled ? 'Disable' : 'Enable'}
        className='mb-0'
      />
      <Button variant='outline-secondary' size='sm' onClick={onEdit}>
        <i className='bi bi-pencil me-1' />
        Edit
      </Button>
      {onDelete && (
        <Button variant='outline-danger' size='sm' onClick={onDelete}>
          <i className='bi bi-trash' />
        </Button>
      )}
    </div>
  </div>
)
